import React, { useEffect, useRef } from 'react';
import { mdiCellphone, mdiWeb, mdiRocketLaunch, mdiChartLine, mdiShieldCheck, mdiCodeBraces, mdiDatabase, mdiCloud } from '@mdi/js';
import Icon from '@mdi/react';
import { motion, useAnimation, useInView } from 'framer-motion';
import Header from "../../components/header";

const Services = () => {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { amount: 0.2 });
  const controls = useAnimation();

  useEffect(() => {
    if (isInView) {
      controls.start("visible");
    }
  }, [isInView, controls]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3
      }
    }
  };

  const itemVariants = {
    hidden: { y: 50, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 10
      }
    }
  };

  const cardVariants = {
    hidden: { y: 50, opacity: 0 },
    visible: (i) => ({
      y: 0,
      opacity: 1,
      transition: {
        delay: i * 0.2,
        type: "spring",
        stiffness: 80,
        damping: 10
      }
    }),
    hover: {
      y: -15,
      boxShadow: "0 25px 50px -12px rgba(59, 130, 246, 0.3)",
      transition: { duration: 0.3 }
    }
  };

  const iconVariants = {
    hover: {
      scale: [1, 1.2, 1],
      rotate: [0, 10, -10, 0],
      transition: {
        duration: 0.8
      }
    },
    float: {
      y: [0, 15, 0],
      transition: {
        duration: 4,
        repeat: Infinity,
        ease: "easeInOut"
      }
    }
  };

  const featureVariants = {
    hidden: { x: -50, opacity: 0 },
    visible: (i) => ({
      x: 0,
      opacity: 1,
      transition: {
        delay: i * 0.15,
        type: "spring",
        stiffness: 100
      }
    })
  };

  return (
    <div className="services-page" ref={sectionRef}>
       <Header />
      <motion.section 
        className="services-section"
        initial="hidden"
        animate={controls}
        variants={containerVariants}
      >
        <div className="services-content">
          <motion.div 
            className="services-text"
            variants={itemVariants}
          >
            <motion.h1 
              className="section-title"
              initial={{ opacity: 0, y: -50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              Our <span className="highlight">Services</span>
            </motion.h1>
            <motion.p 
              className="section-subtitle"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              Expertise in Cutting-Edge Digital Solutions
            </motion.p>
          </motion.div>

          <div className="services-cards">
            <motion.div 
              className="services-card"
              variants={cardVariants}
              custom={0}
              whileHover="hover"
            >
              <motion.div 
                className="card-icon"
                variants={iconVariants}
                whileHover="hover"
                animate="float"
              >
                <Icon path={mdiCellphone} size={2} color="#3b82f6" />
              </motion.div>
              <h2>Mobile App Development</h2>
              <p>
                We design and develop mobile apps for Android and iOS devices. Our team of experts uses the latest technologies to create fast, secure, and user-friendly apps.
              </p>
              <ul>
                <motion.li 
                  variants={featureVariants}
                  custom={0}
                >
                  <Icon path={mdiRocketLaunch} size={0.8} color="#3b82f6" /> Native App Development
                </motion.li>
                <motion.li 
                  variants={featureVariants}
                  custom={1}
                >
                  <Icon path={mdiChartLine} size={0.8} color="#3b82f6" /> Cross-Platform App Development
                </motion.li>
                <motion.li 
                  variants={featureVariants}
                  custom={2}
                >
                  <Icon path={mdiShieldCheck} size={0.8} color="#3b82f6" /> App Maintenance and Updates
                </motion.li>
              </ul>
            </motion.div>

            <motion.div 
              className="services-card"
              variants={cardVariants}
              custom={1}
              whileHover="hover"
            >
              <motion.div 
                className="card-icon"
                variants={iconVariants}
                whileHover="hover"
                animate="float"
              >
                <Icon path={mdiWeb} size={2} color="#3b82f6" />
              </motion.div>
              <h2>Web Development</h2>
              <p>
                We build fast, secure, and scalable websites using the latest web technologies. Our team has experience in developing custom websites, e-commerce platforms, and web applications.
              </p>
              <ul>
                <motion.li 
                  variants={featureVariants}
                  custom={0}
                >
                  <Icon path={mdiCodeBraces} size={0.8} color="#3b82f6" /> Custom Website Development
                </motion.li>
                <motion.li 
                  variants={featureVariants}
                  custom={1}
                >
                  <Icon path={mdiDatabase} size={0.8} color="#3b82f6" /> E-commerce Solutions
                </motion.li>
                <motion.li 
                  variants={featureVariants}
                  custom={2}
                >
                  <Icon path={mdiCloud} size={0.8} color="#3b82f6" /> Web Application Development
                </motion.li>
              </ul>
            </motion.div>
          </div>
        </div>

        {/* Animated background elements */}
        <div className="bg-shapes">
          <div className="shape-1"></div>
          <div className="shape-2"></div>
          <div className="shape-3"></div>
        </div>
      </motion.section>

      <style jsx>{`
        .services-page {
          background: white;
          min-height: 100vh;
          overflow: hidden;
          position: relative;
        }

        .services-section {
          padding: 120px 0;
          position: relative;
          z-index: 1;
        }

        .services-content {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 20px;
        }

        .services-text {
          margin-bottom: 80px;
          text-align: center;
          overflow: hidden;
        }

        .section-title {
          font-size: 4rem;
          font-weight: 800;
          color: #1e293b;
          margin-bottom: 1.5rem;
          line-height: 1.2;
        }

        .section-title .highlight {
          color: #3b82f6;
          background: linear-gradient(90deg, #3b82f6, #6366f1);
          -webkit-background-clip: text;
          background-clip: text;
          -webkit-text-fill-color: transparent;
          position: relative;
          display: inline-block;
        }

        .section-title .highlight::after {
          content: '';
          position: absolute;
          bottom: 5px;
          left: 0;
          width: 100%;
          height: 8px;
          background: rgba(59, 130, 246, 0.2);
          z-index: -1;
          border-radius: 5px;
          transform: scaleX(0);
          transform-origin: left;
          animation: scaleIn 1.5s ease-out forwards;
          animation-delay: 0.8s;
        }

        .section-subtitle {
          font-size: 1.8rem;
          color: #64748b;
          font-weight: 400;
          max-width: 700px;
          margin: 0 auto;
          min-height: 2.5rem;
        }

        .services-cards {
          display: flex;
          justify-content: center;
          align-items: stretch;
          flex-wrap: wrap;
          gap: 40px;
        }

        .services-card {
          background: white;
          padding: 50px 40px;
          border-radius: 20px;
          box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
          width: calc(50% - 40px);
          min-height: 550px;
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
          transition: all 0.3s ease;
          border: 1px solid rgba(0, 0, 0, 0.05);
          position: relative;
          overflow: hidden;
          transform-style: preserve-3d;
        }

        .services-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 5px;
          background: linear-gradient(90deg, #3b82f6, #6366f1);
          z-index: 1;
        }

        .services-card::after {
          content: '';
          position: absolute;
          bottom: 0;
          right: 0;
          width: 100px;
          height: 100px;
          background: radial-gradient(circle, rgba(59, 130, 246, 0.1) 0%, rgba(59, 130, 246, 0) 70%);
          transform: translate(50%, 50%);
          border-radius: 50%;
          z-index: -1;
        }

        .card-icon {
          width: 100px;
          height: 100px;
          background: linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(99, 102, 241, 0.1) 100%);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 40px;
          box-shadow: 0 0 0 rgba(59, 130, 246, 0);
        }

        .services-card h2 {
          font-size: 2rem;
          color: #1e293b;
          margin-bottom: 25px;
          font-weight: 700;
          background: linear-gradient(90deg, #1e293b, #3b82f6);
          -webkit-background-clip: text;
          background-clip: text;
          -webkit-text-fill-color: transparent;
        }

        .services-card p {
          color: #475569;
          font-size: 1.2rem;
          line-height: 1.7;
          margin-bottom: 40px;
        }

        .services-card ul {
          list-style: none;
          padding: 0;
          margin: 0;
          width: 100%;
          text-align: left;
        }

        .services-card li {
          margin-bottom: 20px;
          color: #475569;
          font-size: 1.1rem;
          display: flex;
          align-items: center;
          padding: 15px 20px;
          background: rgba(59, 130, 246, 0.05);
          border-radius: 12px;
        }

        .services-card li :global(svg) {
          margin-right: 15px;
          min-width: 24px;
        }

        .bg-shapes {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          overflow: hidden;
          z-index: -1;
        }

        .shape-1 {
          position: absolute;
          top: 20%;
          left: 10%;
          width: 300px;
          height: 300px;
          background: rgba(59, 130, 246, 0.03);
          border-radius: 30% 70% 70% 30% / 30% 30% 70% 70%;
          animation: float 15s infinite ease-in-out;
        }

        .shape-2 {
          position: absolute;
          bottom: 10%;
          right: 10%;
          width: 200px;
          height: 200px;
          background: rgba(99, 102, 241, 0.03);
          border-radius: 60% 40% 30% 70% / 60% 30% 70% 40%;
          animation: float 12s infinite ease-in-out reverse;
        }

        .shape-3 {
          position: absolute;
          top: 60%;
          left: 50%;
          width: 150px;
          height: 150px;
          background: rgba(59, 130, 246, 0.03);
          border-radius: 50% 50% 50% 50% / 60% 60% 40% 40%;
          animation: float 18s infinite ease-in-out;
        }

        @keyframes float {
          0% {
            transform: translate(0, 0) rotate(0deg);
          }
          50% {
            transform: translate(50px, 50px) rotate(180deg);
          }
          100% {
            transform: translate(0, 0) rotate(360deg);
          }
        }

        @keyframes scaleIn {
          0% {
            transform: scaleX(0);
          }
          100% {
            transform: scaleX(1);
          }
        }

        @media (max-width: 1200px) {
          .services-card {
            padding: 40px 30px;
            min-height: 500px;
          }
        }

        @media (max-width: 992px) {
          .services-card {
            width: 100%;
            min-height: auto;
          }
          
          .section-title {
            font-size: 3.5rem;
          }
        }

        @media (max-width: 768px) {
          .section-title {
            font-size: 2.8rem;
          }
          
          .section-subtitle {
            font-size: 1.4rem;
          }
          
          .services-card {
            padding: 30px 25px;
          }
          
          .card-icon {
            width: 80px;
            height: 80px;
            margin-bottom: 30px;
          }
        }

        @media (max-width: 480px) {
          .section-title {
            font-size: 2.2rem;
          }
          
          .services-card h2 {
            font-size: 1.8rem;
          }
          
          .services-card p {
            font-size: 1rem;
          }
          
          .services-card li {
            font-size: 1rem;
            padding: 12px 15px;
          }
        }
      `}</style>
    </div>
  );
};

export default Services;